import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { ChatMessage as ChatMessageType } from '../../services/chatService';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { textoDaMensagemDeAnexo } from './attachmentMessage';
import ChatMembersManager from './ChatMembersManager';
import ProjectMembersManager from './ProjectMembersManager';
import ModerationPanel from './ModerationPanel';
import { Users, Info, ArrowLeft, X, AtSign, ChevronDown, ChevronUp, Clock, Settings, Shield, Lock } from 'lucide-react';
import { nomeDaConversaDireta } from './conversationList';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { estaNoFim, posicaoDoFim, proximoPassoDoSalto } from './chatScroll';
import { mensagemDeErroDoAnexo } from './attachmentStorage';
import './Chat.css';

interface ChatRoomProps {
  onBack?: () => void;
}

export const ChatRoom: React.FC<ChatRoomProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { currentRoom, messages: chatMessages, members, isLoading, error, sendMessage, sendMessageWithAttachment, loadMoreMessages, addMember, removeMember, updateMemberRole, isCurrentUserAdmin } = useChat();
  const [replyTo, setReplyTo] = useState<ChatMessageType | undefined>(undefined);
  const [attachingFile, setAttachingFile] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [showMembersManager, setShowMembersManager] = useState(false);
  const [showProjectMembersManager, setShowProjectMembersManager] = useState(false);
  const [showModerationPanel, setShowModerationPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  // Se o usuário está lendo a conversa no fim (e não subiu para o histórico).
  const fixadoNoFimRef = useRef(true);
  // Mostra o botão de voltar ao fim quando a pessoa subiu pelo histórico.
  const [longeDoFim, setLongeDoFim] = useState(false);
  const { confirm } = useConfirm();
  const toast = useToast();

  // Determina se o chat atual é um chat de projeto
  const isProjectChat = currentRoom?.type === 'project' && !!currentRoom?.project_id;
  // Conversa privada: leva o nome da outra pessoa e não tem gestão de membros
  // nem moderação — são sempre as mesmas duas pessoas.
  const isDirectChat = currentRoom?.type === 'direct';
  const tituloDaSala = isDirectChat
    ? nomeDaConversaDireta(members, user?.id)
    : currentRoom?.name;

  // Filtrar mensagens para mostrar apenas as mais recentes quando não estiver exibindo histórico completo
  const displayedMessages = showFullHistory 
    ? chatMessages 
    : chatMessages.slice(-15); // Mostrar apenas as 15 mensagens mais recentes quando histórico estiver oculto

  // Ancorar no fim: `scrollIntoView({behavior:'smooth'})` mirava uma posição
  // que ainda ia crescer (avatar/anexo carregando depois) e a animação parava
  // antes da última mensagem. Agora a posição é fixada direto e refeita quando
  // a altura do conteúdo muda.
  const rolarParaFim = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = posicaoDoFim(container);
  }, []);

  useEffect(() => {
    if (fixadoNoFimRef.current) rolarParaFim();
  }, [displayedMessages, rolarParaFim]);

  // Sala nova sempre abre na mensagem mais recente.
  useEffect(() => {
    fixadoNoFimRef.current = true;
    setLongeDoFim(false);
    rolarParaFim();
  }, [currentRoom?.id, rolarParaFim]);

  // Imagem, anexo ou reação mudam a altura depois do render: se o usuário
  // estava no fim, continua no fim.
  useEffect(() => {
    const conteudo = messagesContentRef.current;
    if (!conteudo || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (fixadoNoFimRef.current) rolarParaFim();
    });
    observer.observe(conteudo);
    return () => observer.disconnect();
  }, [rolarParaFim]);

  // Clique na citação de uma resposta leva até a mensagem original. Ela pode
  // estar fora da janela das 15 recentes ou numa página ainda não carregada:
  // o efeito abaixo abre o histórico, busca páginas antigas e só então rola.
  const [saltoPara, setSaltoPara] = useState<string | null>(null);
  const [mensagemDestacada, setMensagemDestacada] = useState<string | null>(null);
  const saltoRef = useRef({ tentativas: 0, totalAntes: 0 });
  // Sem mais histórico, `loadMoreMessages` volta sem mexer no estado e nada
  // re-renderiza: esta rodada força o efeito a reavaliar (e desistir).
  const [rodadaDoSalto, setRodadaDoSalto] = useState(0);

  const irParaMensagem = useCallback((messageId: string) => {
    saltoRef.current = { tentativas: 0, totalAntes: -1 };
    fixadoNoFimRef.current = false;
    setSaltoPara(messageId);
  }, []);

  useEffect(() => {
    if (!saltoPara || isLoading) return;

    const ids = chatMessages.map(m => m.id);
    const { tentativas, totalAntes } = saltoRef.current;
    const semNovidade = tentativas > 0 && ids.length === totalAntes;
    const passo = proximoPassoDoSalto(saltoPara, ids, tentativas, semNovidade);

    if (passo === 'carregar') {
      saltoRef.current = { tentativas: tentativas + 1, totalAntes: ids.length };
      void loadMoreMessages().finally(() => setRodadaDoSalto(n => n + 1));
      return;
    }

    if (passo === 'desistir') {
      setSaltoPara(null);
      toast.error('Mensagem não encontrada', 'A mensagem original não está mais disponível nesta conversa.');
      return;
    }

    const visivel = displayedMessages.some(m => m.id === saltoPara);
    if (!visivel) {
      // Abre o histórico; o efeito roda de novo com a mensagem já na tela.
      setShowFullHistory(true);
      return;
    }

    const alvo = messagesContainerRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(saltoPara)}"]`
    );
    setSaltoPara(null);
    if (!alvo) return;
    fixadoNoFimRef.current = false;
    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setMensagemDestacada(saltoPara);
  }, [saltoPara, rodadaDoSalto, isLoading, chatMessages, displayedMessages, loadMoreMessages, toast]);

  useEffect(() => {
    if (!mensagemDestacada) return;
    const timer = setTimeout(() => setMensagemDestacada(null), 2000);
    return () => clearTimeout(timer);
  }, [mensagemDestacada]);

  // Efeito para rolar para o topo ao mostrar histórico completo
  useEffect(() => {
    // Abriu o histórico para saltar até uma citação: quem rola é o salto.
    if (saltoPara) return;
    if (showFullHistory && messagesTopRef.current) {
      // Subiu para ler o histórico: nada de puxar de volta para baixo.
      fixadoNoFimRef.current = false;
      messagesTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    // Só a abertura do histórico dispara a ida ao topo; `saltoPara` é lido do
    // mesmo render de propósito (o salto o limpa antes deste efeito rodar).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFullHistory]);

  // Efeito para depuração
  // Verificar se o usuário é admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user && currentRoom) {
        const adminStatus = await isCurrentUserAdmin();
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
    // Depende de `user?.id`, não do objeto `user` (ver App.tsx) — evita reverificar
    // toda vez que a aba volta a ficar visível.
  }, [user?.id, currentRoom, isCurrentUserAdmin]);

  useEffect(() => {
    if (currentRoom) {
      console.log('ChatRoom: currentRoom', currentRoom);
      console.log('ChatRoom: membros', members);
      
      if (members.length > 0) {
        const membersList = members.map(m => ({
          id: m.user_auth_id,
          // Usar informações do objeto user quando disponível
          name: m.user && typeof m.user === 'object' ? (m.user.name || 'Sem nome') : 'Desconhecido',
          role: m.role,
          auth_id: m.user_auth_id
        }));
        console.log('Membros do chat formatados:', membersList);
        
        // Verificar o formato de cada membro para depuração
        members.forEach((member, index) => {
          console.log(`Membro #${index + 1}:`, {
            id: member.user_auth_id,
            user: member.user,
            // Se user é um objeto, extrair propriedades relevantes
            userName: member.user && typeof member.user === 'object' 
              ? (member.user.name || 'Sem nome no objeto user') 
              : 'user não é objeto',
            role: member.role,
          });
        });
      }
    }
  }, [currentRoom, members]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    fixadoNoFimRef.current = estaNoFim(container);
    setLongeDoFim(!fixadoNoFimRef.current);

    if (container.scrollTop === 0 && !isLoading && showFullHistory) {
      loadMoreMessages();
    }
  };

  const handleSendMessage = async (text: string, mentionedUserIds?: string[]) => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    if (!text.trim()) return;

    try {
      await sendMessage(text, replyTo?.id, mentionedUserIds);
      setReplyTo(undefined);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleAttachFile = async (file: File, caption?: string) => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setAttachingFile(true);
      // Enviamos uma mensagem com o arquivo anexado. Sem legenda, o nome do
      // arquivo vira o texto — menos no áudio, que vai sem texto nenhum.
      const messageText = textoDaMensagemDeAnexo(file.name, file.type, caption);
      await sendMessageWithAttachment(messageText, file, replyTo?.id);
      setReplyTo(undefined);
    } catch (err) {
      // Antes o upload podia falhar em silêncio: o erro parava no console e a
      // mensagem seguia enviada sem o arquivo, então quem mandou achava que
      // tinha anexado. Agora nada é enviado sem o arquivo, e a falha aparece.
      console.error('Failed to attach file:', err);
      toast.error('Não foi possível anexar o arquivo', mensagemDeErroDoAnexo(err));
      throw err;
    } finally {
      setAttachingFile(false);
    }
  };

  const handleAddMember = async (member: { id: string, name: string, role: 'admin' | 'member' | 'guest' }) => {
    try {
      await addMember(member.id, member.role);
    } catch (err) {
      console.error('Failed to add member:', err);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      // Verificar se o usuário atual é administrador antes de remover
      const isAdmin = await isCurrentUserAdmin();
      if (!isAdmin) {
        console.error('Usuário não tem permissão para remover membros');
        return;
      }

      // Confirmar a remoção
      const memberToRemove = members.find(m => m.user_auth_id === memberId);
      const memberName = memberToRemove?.user?.name || 'este membro';

      const ok = await confirm({
        message: `Tem certeza que deseja remover ${memberName} da sala?`,
        variant: 'danger',
      });
      if (ok) {
        await removeMember(memberId);
        console.log('Membro removido com sucesso:', memberId);
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: 'admin' | 'member' | 'guest') => {
    try {
      // Verificar se o usuário atual é administrador antes de alterar papel
      const isAdmin = await isCurrentUserAdmin();
      if (!isAdmin) {
        console.error('Usuário não tem permissão para alterar papéis de membros');
        return;
      }

      await updateMemberRole(memberId, newRole);
      console.log('Papel do membro atualizado com sucesso:', memberId, newRole);
    } catch (err) {
      console.error('Failed to update member role:', err);
    }
  };

  // Verificar se existem menções para o usuário atual
  const hasMentions = chatMessages.some(
    message => message.mentions?.some(
      mention => mention.user_auth_id === user?.id && !mention.is_read
    )
  );

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Please log in to access the chat.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <p className="text-nk-error">{error}</p>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
        )}
      </div>
    );
  }

  if (!currentRoom) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Select a chat room to start messaging</p>
      </div>
    );
  }

  // Determinar se há histórico oculto
  const hasHiddenHistory = chatMessages.length > displayedMessages.length;

  return (
    <div className="chat-room integrated">
      {/* Header */}
      <div className="chat-room-header">
        {onBack && (
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 text-gray-700 hover:text-gray-900"
            title="Voltar para as conversas"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium hidden sm:inline">Voltar</span>
          </button>
        )}
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {tituloDaSala}
          </h2>
          {isDirectChat && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Lock className="w-3 h-3" />
              Conversa privada
            </span>
          )}
          {hasMentions && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-nk-error mt-1">
              <AtSign className="w-3 h-3 mr-1" />
              Mencionado
            </span>
          )}
        </div>
        {!isDirectChat && (
        <div className="flex gap-2">
          <button
            onClick={() => {
              if (isProjectChat) {
                setShowProjectMembersManager(true);
              } else {
                setShowMembersManager(true);
              }
            }}
            className="p-2 rounded-lg hover:bg-gray-100 flex items-center gap-2"
            title="Membros"
          >
            <Users className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Membros</span>
          </button>
          
          {isAdmin && (
            <button
              onClick={() => setShowModerationPanel(true)}
              className="p-2 rounded-lg hover:bg-gray-100 flex items-center gap-2"
              title="Moderação"
            >
              <Shield className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Moderação</span>
            </button>
          )}
        </div>
        )}
      </div>

      {/* Mensagens */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="chat-room-messages"
      >
        <div className="space-y-2" ref={messagesContentRef}>
            {isLoading && chatMessages.length === 0 ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
              </div>
            ) : (
              <>
                {hasHiddenHistory && !showFullHistory && (
                  <button 
                    onClick={() => setShowFullHistory(true)}
                    className="w-full py-3 mb-4 flex items-center justify-center gap-2 text-sm text-coral bg-coral-soft hover:bg-coral-soft rounded-lg transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                    <span>Ver histórico completo ({chatMessages.length - displayedMessages.length} mensagens anteriores)</span>
                  </button>
                )}
                
                {showFullHistory && chatMessages.length > 15 && (
                  <button 
                    onClick={() => {
                      setShowFullHistory(false);
                      fixadoNoFimRef.current = true;
                      setTimeout(rolarParaFim, 100);
                    }}
                    className="w-full py-3 mb-4 flex items-center justify-center gap-2 text-sm text-gray-600 bg-white hover:bg-gray-50 rounded-lg border transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                    <span>Ocultar histórico antigo</span>
                  </button>
                )}

                {displayedMessages && displayedMessages.length > 0 ? (
                  <>
                    {/* Ref para o topo das mensagens */}
                    {showFullHistory && <div ref={messagesTopRef} />}
                    
                    {!showFullHistory && hasHiddenHistory && (
                      <div className="flex items-center justify-center py-4 mb-4">
                        <div className="border-t border-gray-300 flex-grow mr-4"></div>
                        <span className="text-xs text-gray-500 flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full">
                          <Clock className="w-3 h-3" />
                          Mensagens mais recentes
                        </span>
                        <div className="border-t border-gray-300 flex-grow ml-4"></div>
                      </div>
                    )}
                    
                    {displayedMessages.map((message: ChatMessageType) => {
                      // Obter o nome do usuário da mensagem a partir do objeto user
                      const userName = message.user && message.user.name 
                        ? message.user.name 
                        : 'Usuário';
                      
                      // Log apenas para mensagens sem nome de usuário
                      if (!message.user || !message.user.name) {
                        console.log('Mensagem com nome de usuário ausente:', message);
                      }
                      
                      return (
                        <ChatMessage
                          key={message.id}
                          message={message}
                          isOwnMessage={message.user_auth_id === user.id}
                          onReply={() => setReplyTo(message)}
                          onJumpToMessage={irParaMensagem}
                          highlighted={mensagemDestacada === message.id}
                        />
                      );
                    })}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[200px] text-gray-500">
                    <div className="text-center">
                      <h3 className="text-lg font-medium mb-2">Nenhuma mensagem ainda</h3>
                      <p className="text-sm">Seja o primeiro a enviar uma mensagem!</p>
                    </div>
                  </div>
                )}
                
                {/* Espaçamento no final para a última mensagem: a barra de
                    ações aparece abaixo da bolha e não pode ser cortada pela
                    borda da lista. */}
                <div className="h-6"></div>
                <div ref={messagesEndRef} />
              </>
            )}
        </div>
      </div>

      {/* Input */}
      <div className="chat-room-input relative" ref={inputAreaRef}>
        {longeDoFim && (
          <button
            onClick={() => {
              fixadoNoFimRef.current = true;
              setLongeDoFim(false);
              rolarParaFim();
              // Volta para escrever: o foco também traz a caixa de texto para a tela.
              inputAreaRef.current?.querySelector('textarea')?.focus();
            }}
            className="absolute -top-14 right-6 p-2.5 rounded-full bg-white border shadow-md text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
            title="Voltar para escrever"
            aria-label="Voltar para escrever"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        )}
        <ChatInput
          onSendMessage={handleSendMessage}
          onAttachFile={handleAttachFile}
          disabled={isLoading || attachingFile}
          replyingTo={replyTo}
          onCancelReply={() => setReplyTo(undefined)}
          isAttaching={attachingFile}
        />
      </div>

      {/* Modal de gerenciamento de membros */}
      <ChatMembersManager
        isOpen={showMembersManager}
        onClose={() => setShowMembersManager(false)}
        members={members.map(m => ({
          id: m.user_auth_id,
          name: m.user?.name || 'Membro do Chat',
          role: m.role as 'admin' | 'member' | 'guest',
          avatar: m.user?.avatar_url
        }))}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onUpdateMemberRole={handleUpdateMemberRole}
      />

      {/* Modal de gerenciamento de membros do projeto */}
      <ProjectMembersManager
        isOpen={showProjectMembersManager}
        onClose={() => setShowProjectMembersManager(false)}
      />

      {/* Painel de Moderação */}
      <ModerationPanel
        isOpen={showModerationPanel}
        onClose={() => setShowModerationPanel(false)}
        roomId={currentRoom?.id}
      />
    </div>
  );
};

export default ChatRoom;